import { useEffect, useMemo, useState } from "react";
import { InputTable, InputColumnTableProps, Combobox, Modal, ModalWidth, GetItemsProps } from "medere-ui-framework";
import { Button } from "primereact/button";
import { AssociatedBox, defaultAssociatedBox, Establishment, PatientCaller, Totem } from "../../../../../interface/interface";
import { getAllHospitalLocationBySectorId } from "../../../../../servicesHooks/Location/useLocationConfigService";
import useResizeListener from "../../../../../hooks/useResizeListener";
import LocationMobileActionMenu, { LocationMobileActionItem } from "../../components/LocationMobileActionMenu";

interface Props {
    associatedBoxes: AssociatedBox[];
    establishments: Establishment[];
    onChange: (newAssociatedBoxes: AssociatedBox[]) => void;
    preSelectedEstablishment: Establishment;
    itemToEdit?: PatientCaller | Totem;
    modalWidth?: ModalWidth;
}

interface AssociatedBoxErrors {
    establishment?: string;
    sector?: string;
    box?: string;
}

function getInitialEstablishment(preSelectedEstablishment: Establishment | null | undefined) {
    if (!preSelectedEstablishment) return null;
    if ((preSelectedEstablishment.buildingid ?? 0) <= 0) return null;
    if (preSelectedEstablishment.active === false) return null;
    return preSelectedEstablishment;
}

export function AssociatedBoxTable({ associatedBoxes, establishments, onChange, preSelectedEstablishment, itemToEdit, modalWidth = "w-medium" }: Props) {
    const isMobile = useResizeListener(768);
    const [associatedBoxItem, setAssociatedBoxItem] = useState<AssociatedBox>({
        ...defaultAssociatedBox,
        establishment: getInitialEstablishment(preSelectedEstablishment)
    });
    const [associatedBoxErrors, setAssociatedBoxErrors] = useState<AssociatedBoxErrors>({});
    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [mobileModalVisible, setMobileModalVisible] = useState(false);

    const normalizedAssociatedBoxes = useMemo(() => associatedBoxes ?? [], [associatedBoxes]);
    const availableEstablishments = useMemo(() => establishments ?? [], [establishments]);
    const availableSectors = useMemo(() => associatedBoxItem.establishment?.sectors ?? [], [associatedBoxItem.establishment]);

    const getSectorItems = ({ filterValue }: GetItemsProps) => { //availableSectors ya está cargado en memoria, se filtra localmente en vez de pegarle al backend
        const query = (filterValue ?? "").trim().toLowerCase();
        const filtered = query ? availableSectors.filter((s) => (s.name ?? "").toLowerCase().includes(query)) : availableSectors;
        return Promise.resolve(filtered);
    };

    const getEstablishmentItems = ({ filterValue }: GetItemsProps) => { //availableEstablishments ya está cargado en memoria, se filtra localmente en vez de pegarle al backend
        const query = (filterValue ?? "").trim().toLowerCase();
        const filtered = query ? availableEstablishments.filter((e) => (e.buildingname ?? "").toLowerCase().includes(query)) : availableEstablishments;
        return Promise.resolve(filtered);
    };

    const lockEstablishment = ((!!itemToEdit && normalizedAssociatedBoxes.length > 0) || normalizedAssociatedBoxes.length > 0);

    const resetEditor = (preserveLockedEstablishment = false) => {
        const lockedEstablishment = normalizedAssociatedBoxes.length > 0 ? normalizedAssociatedBoxes[0].establishment : null;
        const fallbackEstablishment = getInitialEstablishment(preSelectedEstablishment);
        const establishmentToUse = preserveLockedEstablishment ? (lockedEstablishment ?? fallbackEstablishment) : fallbackEstablishment;

        setAssociatedBoxItem({
            ...defaultAssociatedBox,
            establishment: establishmentToUse
        });
        setAssociatedBoxErrors({});
        setEditIndex(null);
    };

    const closeMobileModal = () => {
        setMobileModalVisible(false);
        resetEditor();
    };

    useEffect(() => {
        if (!preSelectedEstablishment) return;
        if ((preSelectedEstablishment.buildingid ?? 0) <= 0) return;
        if (preSelectedEstablishment.active !== false) return;
        setAssociatedBoxItem({ ...defaultAssociatedBox, establishment: null, sector: null, box: null });
        setAssociatedBoxErrors(prev => ({ ...prev, establishment: "Establecimiento inactivo" }));
    }, [preSelectedEstablishment]);

    const validateAssociatedBox = (associatedBox: AssociatedBox): AssociatedBoxErrors => {
        const errors: AssociatedBoxErrors = {};
        if (!associatedBox.establishment || associatedBox.establishment.buildingid <= 0) errors.establishment = "Este campo es requerido";
        if (!associatedBox.sector || associatedBox.sector.sector <= 0) errors.sector = "Este campo es requerido";
        if (!associatedBox.box || associatedBox.box.hospitallocation <= 0) errors.box = "Este campo es requerido";
        return errors;
    };

    const addOrUpdateAssociatedBox = async () => {
        const errors = validateAssociatedBox(associatedBoxItem);
        setAssociatedBoxErrors(errors);
        const hasErrors = Object.keys(errors).length > 0;
        if (hasErrors) return false;

        if (editIndex === null) {
            onChange([...normalizedAssociatedBoxes, associatedBoxItem]);
            if (isMobile) {
                closeMobileModal();
            } else {
                setAssociatedBoxItem(prev => ({ ...prev, box: null }));
                setAssociatedBoxErrors({});
            }
            return true;
        }

        onChange(normalizedAssociatedBoxes.map((item, index) => index === editIndex ? associatedBoxItem : item));
        if (isMobile) {
            closeMobileModal();
        } else {
            resetEditor(true);
        }
        return true;
    };

    const handleEditAssociatedBox = (item: AssociatedBox, index: number, openModal = false) => {
        setAssociatedBoxItem(item);
        setAssociatedBoxErrors({});
        setEditIndex(index);
        if (openModal) setMobileModalVisible(true);
    };

    const removeAssociatedBox = (item: AssociatedBox, idx: number) => {
        onChange(normalizedAssociatedBoxes.filter((_, i) => i !== idx));
        if (editIndex === null) return;
        if (editIndex === idx) {
            resetEditor(true);
            return;
        }
        if (idx < editIndex) setEditIndex(editIndex - 1);
    };

    const boxCols: InputColumnTableProps<AssociatedBox>[] = [
        {
            header: "Establecimiento",
            className: "w-1/2",
            body: (i) => i.establishment.buildingname,
            input: (
                <Combobox
                    key={"comboestablishmentid"}
                    className={lockEstablishment ? "" : "border2"}
                    getItems={getEstablishmentItems}
                    value={associatedBoxItem.establishment}
                    setValue={(e) => {
                        setAssociatedBoxItem({ ...associatedBoxItem, establishment: e, sector: null, box: null });
                        setAssociatedBoxErrors({ ...associatedBoxErrors, establishment: "", sector: "", box: "" });
                    }}
                    optionLabel="buildingname"
                    disable={lockEstablishment}
                    error={!!associatedBoxErrors.establishment}
                    caption={associatedBoxErrors.establishment}
                />
            ),
        },
        {
            header: "Sector",
            className: "w-1/2",
            body: (i) => i.sector?.name,
            input: (
                <Combobox
                    key={"combosectorid"}
                    className={(!associatedBoxItem.establishment || associatedBoxItem.establishment.buildingid <= 0) ? "" : "border2"}
                    getItems={getSectorItems}
                    value={associatedBoxItem.sector}
                    setValue={(e) => {
                        setAssociatedBoxItem({ ...associatedBoxItem, sector: e, box: null });
                        setAssociatedBoxErrors({ ...associatedBoxErrors, sector: "", box: "" });
                    }}
                    optionLabel="name"
                    disable={!associatedBoxItem.establishment || associatedBoxItem.establishment.buildingid <= 0}
                    error={!!associatedBoxErrors.sector}
                    caption={associatedBoxErrors.sector}
                />
            ),
        },
        {
            header: "Puesto",
            className: "w-1/2",
            body: (i) => i.box.description,
            input: (
                <Combobox
                    key={"comboofficeboxid"}
                    className={(!associatedBoxItem.establishment || associatedBoxItem.establishment.buildingid <= 0 || !associatedBoxItem.sector || associatedBoxItem.sector.sector <= 0) ? "" : "border2"}
                    getItems={getAllHospitalLocationBySectorId}
                    params={{ sectorId: associatedBoxItem.sector?.sector }}
                    value={associatedBoxItem.box}
                    setValue={(e) => {
                        setAssociatedBoxItem({ ...associatedBoxItem, box: e });
                        setAssociatedBoxErrors({ ...associatedBoxErrors, box: "" });
                    }}
                    optionLabel="description"
                    disable={!associatedBoxItem.establishment || associatedBoxItem.establishment.buildingid <= 0 || !associatedBoxItem.sector || associatedBoxItem.sector.sector <= 0}
                    error={!!associatedBoxErrors.box}
                    caption={associatedBoxErrors.box}
                />
            ),
        },
        {
            header: "",
            className: "",
            body: (_item, idx) => (
                <Button id="removeofficeboxbuttonid" icon="pi pi-trash" className="p-button-text" onClick={() => removeAssociatedBox(_item, idx)} />
            ),
        },
    ];

    if (!isMobile) {
        return (
            <div>
                <InputTable
                    columnGap={4}
                    iconAddBtn="pi pi-plus-circle text-2xl"
                    headerDivClassName="font-semibold bg-bluegray-50"
                    dividerClassName="my-1 border-divider"
                    buttonColumnWidth={40}
                    items={normalizedAssociatedBoxes}
                    columns={boxCols}
                    onAdd={addOrUpdateAssociatedBox}
                    divider
                    dividerInFirstItem
                />
            </div>
        );
    }

    function formatCardValue(value: string | null | undefined, emptyText = "Sin datos") {
        const normalized = String(value ?? "").trim();
        return normalized !== "" ? normalized : emptyText;
    }

    return (
        <div>
            <div className="location-mobile-inline-actions">
                <Button
                    className="location-mobile-add-btn"
                    icon="pi pi-plus-circle"
                    label="Agregar"
                    onClick={() => {
                        resetEditor(true);
                        setMobileModalVisible(true);
                    }}
                />
            </div>

            <div className="location-mobile-card-list">
                {normalizedAssociatedBoxes.map((item, index) => {
                    const menuItems: LocationMobileActionItem[] = [
                        { label: "Editar", icon: "pi pi-file-edit", action: () => handleEditAssociatedBox(item, index, true) },
                        { label: "Eliminar", icon: "pi pi-trash", action: () => removeAssociatedBox(item, index) },
                    ];

                    return (
                        <article key={`associated-box-${item.box?.hospitallocation ?? index}-${index}`} className="location-mobile-card">
                            <div className="location-mobile-card-header">
                                <h4 className="location-mobile-card-title">{formatCardValue(item.box?.description, "Sin puesto")}</h4>
                                <LocationMobileActionMenu items={menuItems} ariaLabel="Opciones del puesto asociado" />
                            </div>
                            <div className="location-mobile-card-row">
                                <span className="location-mobile-card-key">Establecimiento</span>
                                <span className="location-mobile-card-value">{formatCardValue(item.establishment?.buildingname)}</span>
                            </div>
                            <div className="location-mobile-card-row">
                                <span className="location-mobile-card-key">Sector</span>
                                <span className="location-mobile-card-value">{formatCardValue(item.sector?.name)}</span>
                            </div>
                        </article>
                    );
                })}
                {normalizedAssociatedBoxes.length === 0 && (
                    <p className="location-mobile-empty">No hay puestos asociados.</p>
                )}
            </div>

            <Modal
                className="location-mobile-editor-modal location-mobile-nested-editor-modal"
                visible={mobileModalVisible}
                setVisible={setMobileModalVisible}
                onHideCustom={closeMobileModal}
                width={modalWidth}
                header={<h1 className="info-dark text-2xl">{editIndex === null ? "Agregar puesto" : "Editar puesto"}</h1>}
            >
                <div className="location-config-view location-mobile-modal-content">
                    <div className="location-mobile-modal-fields">
                        <div>
                            <p>Establecimiento</p>
                            <Combobox
                                className={lockEstablishment ? "" : "border2"}
                                getItems={getEstablishmentItems}
                                value={associatedBoxItem.establishment}
                                setValue={(e) => {
                                    setAssociatedBoxItem({ ...associatedBoxItem, establishment: e, sector: null, box: null });
                                    setAssociatedBoxErrors({ ...associatedBoxErrors, establishment: "", sector: "", box: "" });
                                }}
                                optionLabel="buildingname"
                                disable={lockEstablishment}
                                error={!!associatedBoxErrors.establishment}
                                caption={associatedBoxErrors.establishment}
                            />
                        </div>
                        <div>
                            <p>Sector</p>
                            <Combobox
                                className={(!associatedBoxItem.establishment || associatedBoxItem.establishment.buildingid <= 0) ? "" : "border2"}
                                getItems={getSectorItems}
                                value={associatedBoxItem.sector}
                                setValue={(e) => {
                                    setAssociatedBoxItem({ ...associatedBoxItem, sector: e, box: null });
                                    setAssociatedBoxErrors({ ...associatedBoxErrors, sector: "", box: "" });
                                }}
                                optionLabel="name"
                                disable={!associatedBoxItem.establishment || associatedBoxItem.establishment.buildingid <= 0}
                                error={!!associatedBoxErrors.sector}
                                caption={associatedBoxErrors.sector}
                            />
                        </div>
                        <div>
                            <p>Puesto</p>
                            <Combobox
                                className={(!associatedBoxItem.establishment || associatedBoxItem.establishment.buildingid <= 0 || !associatedBoxItem.sector || associatedBoxItem.sector.sector <= 0) ? "" : "border2"}
                                getItems={getAllHospitalLocationBySectorId}
                                params={{ sectorId: associatedBoxItem.sector?.sector }}
                                value={associatedBoxItem.box}
                                setValue={(e) => {
                                    setAssociatedBoxItem({ ...associatedBoxItem, box: e });
                                    setAssociatedBoxErrors({ ...associatedBoxErrors, box: "" });
                                }}
                                optionLabel="description"
                                disable={!associatedBoxItem.establishment || associatedBoxItem.establishment.buildingid <= 0 || !associatedBoxItem.sector || associatedBoxItem.sector.sector <= 0}
                                error={!!associatedBoxErrors.box}
                                caption={associatedBoxErrors.box}
                            />
                        </div>
                    </div>
                    <div className="flex justify-content-end align-items-center gap-2 mt-2 location-mobile-modal-actions">
                        <Button label="Cancelar" onClick={closeMobileModal} className="btnCancel2 btnWidth" />
                        <Button label={editIndex === null ? "Agregar" : "Guardar"} onClick={() => { void addOrUpdateAssociatedBox(); }} className="btnContinue btnWidth" />
                    </div>
                </div>
            </Modal>
        </div>
    );
}

